// Hands a generated file (CSV/Excel/PDF export, print-to-PDF, etc.) off to the user —
// a plain browser download on web, or the native share sheet (via a Cache-directory
// temp file) on Android/iOS, where there is no filesystem download UX to fall back to.
import { Capacitor } from '@capacitor/core'
import { Directory, Filesystem } from '@capacitor/filesystem'
import { Share } from '@capacitor/share'
import { toast } from 'sonner'

import i18n from '@/i18n'
import { beginNativeUiHandoff, endNativeUiHandoff } from '@/libs/nativeUiHandoff'

/** Today's exact web download logic: an object URL + a programmatic `<a download>` click,
 *  then revoke the object URL once the click has been dispatched. */
function downloadBlobOnWeb(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/** Reads a Blob into a bare base64 string (no `data:<mime>;base64,` prefix) — the format
 *  `Filesystem.writeFile` expects for binary data on native platforms. */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const result = reader.result
      if (typeof result !== 'string') {
        reject(new Error('Failed to read blob as base64'))
        return
      }
      resolve(result.slice(result.indexOf(',') + 1))
    }
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read blob'))
    reader.readAsDataURL(blob)
  })
}

/** The native `Share.share()` rejects with "Share canceled" (Android/iOS) when the user
 *  dismisses the share sheet, and the web Share API rejects with an `AbortError` — neither
 *  is a real failure, so it must not surface as an error toast. */
function isShareCancelled(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') return true
  const message = error instanceof Error ? error.message : String(error)
  return message.toLowerCase().includes('cancel')
}

async function writeBlobToCache(blob: Blob, filename: string, mimeType: string): Promise<string> {
  const base64 = await blobToBase64(new Blob([blob], { type: mimeType }))
  const { uri } = await Filesystem.writeFile({
    path: filename,
    data: base64,
    directory: Directory.Cache,
  })
  return uri
}

async function shareBlobNatively(blob: Blob, filename: string, mimeType: string): Promise<void> {
  let uri: string
  try {
    uri = await writeBlobToCache(blob, filename, mimeType)
  } catch {
    toast.error(i18n.t('filters.exportFailed'))
    return
  }

  // The native share sheet legitimately backgrounds the host app — mark the hand-off so
  // `AppLockGate` doesn't treat it as the user leaving and force a re-unlock the moment
  // the share sheet closes.
  beginNativeUiHandoff()
  try {
    await Share.share({ url: uri, title: filename })
  } catch (error) {
    if (isShareCancelled(error)) return
    toast.error(i18n.t('filters.exportFailed'))
  } finally {
    endNativeUiHandoff()
  }
}

/** Save (web) or share (native) a generated file. Fire-and-forget by design, matching the
 *  synchronous `downloadBlob` call sites this replaces — on native the write + share happen
 *  asynchronously behind the scenes, with failures surfaced via a toast. */
export function saveOrShareBlob(blob: Blob, filename: string, mimeType: string): void {
  if (!Capacitor.isNativePlatform()) {
    downloadBlobOnWeb(blob, filename)
    return
  }
  void shareBlobNatively(blob, filename, mimeType)
}
